import React, { useEffect, useState } from 'react';
import { PrintIcon } from '../../../utils/TableActionIcons';
import { CreditInvoiceColumns, exportCreditInvoicesToExcelColumns } from '../../../constants/BillingConstants';
import { useNavigate } from 'react-router-dom';
import CustomPagination from '../../../shared/CustomPagination';
import CustomTable from '../../../shared/CustomTable';
import { useGetCreditNotesInvoicesQuery } from '../../../redux/api/modules/billingApi';
import BillingFilters from './BillingFilters';
import { useTranslation } from 'react-i18next';
import { fetchData } from '../../../helpers/fetchData';
import { useSelector } from 'react-redux';
import { hasBillingSearchFilters, omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from '../../../utils/businessLevelAccess';
import { IoIosSend } from 'react-icons/io';
import toast from 'react-hot-toast';
import usePersistentBillingFilters from '../../../hooks/usePersistentBillingFilters';
import { MdCancel } from 'react-icons/md';
import CheckPermission from '../../../hooks/CheckPermission';
import ReusableModal from '../../../shared/ReuseableModal';
import CancelCreditInvoice from './CancelCreditInvoice';
const ConfirmationModal = React.lazy(() => import('../../../shared/ConfirmationModal'));

function CreditNotesInvoice() {
  const { hasPermission } = CheckPermission();
  const authUser = useSelector(state => state?.auth?.user);
  const skipInitialBillingLoad = shouldSkipInitialBillingLoad(authUser);
  const [payload, setPayload, resetPayload, appliedPayload, applyFilters] = usePersistentBillingFilters('creditCash', { per_page: 10 });

  const [searchQuery, setSearchQuery] = useState(payload?.query || '');
  const { t } = useTranslation();
  const [selectedInvoive, setSelectedInvoice] = useState(null);
  const [openSendToZatcaModal, setOpenSendToZatcaModal] = useState(false);
  const [zatcaLoading, setZatcaLoading] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const auth = useSelector(state => state?.auth);
  const queryPayload = React.useMemo(() => {
    const formData = {
      ...appliedPayload,
      filters: { ...appliedPayload?.filters }
    };
    formData.filters.credit_cash_invoice_number = appliedPayload?.filters?.invoice_number;
    delete formData?.filters?.invoice_number;
    return formData;
  }, [appliedPayload]);

  const {
    data,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetCreditNotesInvoicesQuery(queryPayload, {
    skip: skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)
  });

  const loading = queryLoading || queryFetching;
  const navigate = useNavigate();

  const actionsCoumn = {
    body: actionsTemplate,
    header: t('Actions')
  };

  const fetchInitialData = () => {
    refetch();
  };

  const columnsData = [actionsCoumn, ...CreditInvoiceColumns(t, auth)];
  function actionsTemplate(rowData) {
    return (
      <div className='flex items-center gap-1'>
        <PrintIcon
          title={t('PrintInvoice')}
          iconSize='20'
          onClick={() => {
            const isNewInvoiceNumber = rowData?.credit_cash_invoice_number
            navigate(`/print-invoice/${rowData?.credit_cash_invoice_number || rowData?.invoice_number}`, {
              state: {
                invoiceNumber: rowData?.credit_cash_invoice_number || rowData?.invoice_number,
                cancel: true,
                isCreditCash: true,
                isNewInvoiceNumber: isNewInvoiceNumber,
                activeTabIndex: 0,
                activeChildTabIndex: 1
              }
            })
          }}
        />
        {hasPermission('zatca', 'get') &&
          rowData?.zatca_status != 'REPORTED' &&
          !rowData?.is_cancelled && (
          <IoIosSend
            className={`text-green-500  cursor-pointer `}
            title={t('Send to zatca')}
            size={20}
            onClick={async () => {
              setOpenSendToZatcaModal(true);
              setSelectedInvoice(rowData);
            }}
          />
        )}
        {!rowData?.is_cancelled &&
          (hasPermission('billing', 'update') ||
            hasPermission('billing', 'delete') ||
            hasPermission('invoices', 'update')) && (
          <MdCancel
            title={t('Cancel Invoice')}
            className='text-red-500 cursor-pointer'
            size='18'
            onClick={() => {
              setSelectedInvoiceData(rowData);
              setOpenCancelModal(true);
            }}
          />
        )}
      </div>
    );
  }
  const onPageChange = async (newPage, newRowsPerPage) => {
    setPayload(prevPayload => ({
      ...prevPayload,
      page: newPage,
      per_page: newRowsPerPage
    }));
  };

  

  const handleSendToZatca = async () => {
    if (!hasPermission('zatca', 'get')) return;
    if (!selectedInvoive?.credit_cash_invoice_number) {
      toast.error('No invoice number found.');
      return;
    }
    setZatcaLoading(true);
    const response = await fetchData('POST', '/v3/invoices/sendReturnPatientInvoiceToZatca', {
      credit_cash_invoice_number: selectedInvoive?.credit_cash_invoice_number
    });
    if (response?.message) {
      setOpenSendToZatcaModal(false);
      setSelectedInvoice(null);
      toast.success(response?.message);
      fetchInitialData();
    }
    setZatcaLoading(false);
  };

  return (
    <div>
      <div className='flex xl:justify-between mb-2 xl:mb-0'>
      <BillingFilters
        applyFilters={applyFilters}
          payloadData={payload}
          setPayloadData={setPayload}
          refetch={refetch}
          invoiceType='creditCash'
          setSearchQuery={setSearchQuery}
          fetchInitialData={fetchInitialData}
          searchQuery={searchQuery}
          resetFilters={() => {
            resetPayload();
            setSearchQuery('');
          }}
          name={'credti_notes_invoices'}
          exportOptionsFormat={exportCreditInvoicesToExcelColumns}
          exportApiUrl={'/v3/invoices/exportPatientCancelInoviceExcel'}
        />
      </div>

      <CustomTable 
        data={data?.data || []} 
        columns={columnsData} 
        isLoading={loading}
        rowClassName={(rowData) => rowData?.is_cancelled ? '!bg-red-100' : ''}
      />
      {data?.data?.length > 0 && (
        <CustomPagination
          currentPage={data?.page || payload?.page || 1}
          rowsPerPage={data?.per_page || payload?.per_page || 20}
          totalRecords={data?.totalRecords}
          totalPages={data?.totalPages}
          onPageChange={onPageChange}
        />
      )}

      <ConfirmationModal
        visible={openSendToZatcaModal}
        setVisible={setOpenSendToZatcaModal}
        header={'Confirmation'}
        message={`Are you sure you want to send invoice ${selectedInvoive?.credit_cash_invoice_number} to Zatca?`}
        acceptLabel={'Send To Zatca'}
        rejectLabel={'Cancel'}
        onAccept={handleSendToZatca}
        isLoading={zatcaLoading}
      />

      <ReusableModal
        isVisible={openCancelModal}
        title={t(`Cancel Invoice ${selectedInvoiceData?.credit_cash_invoice_number}`)}
        HeaderIcon={MdCancel}
        position='top'
        onClose={() => {
          setOpenCancelModal(false);
          setSelectedInvoiceData(null);
        }}
      >
        <CancelCreditInvoice
          selectedInvoiceData={selectedInvoiceData}
          refetch={fetchInitialData}
          setOpenCancelModal={setOpenCancelModal}
          setSelectedInvoiceData={setSelectedInvoiceData}
        />
      </ReusableModal>
    </div>
  );
}

export default CreditNotesInvoice;
